import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HitApi, HitApiFormData } from '../../../Api/ApiHit';
import { addAppCategory, updateAppCategory, searchAppCategory, uploadSingleNew, projectKey, secretKey } from '../../../constant/Constant';
import { getAcessToken } from '../../../storage/Storage';
import { ArrowLeft, Plus, Trash2, Save, Upload, Loader2, Image, Palette } from 'lucide-react';
import toast from 'react-hot-toast';

const ColorGradientPicker = ({ value, onChange, height = 'h-32' }) => {
  const [isGradient, setIsGradient] = useState(() => value?.includes('gradient'));
  const [gradientColors, setGradientColors] = useState(() => {
    if (value?.includes('gradient')) {
      const matches = value.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
      return { color1: matches?.[0] || '#000000', color2: matches?.[1] || '#ffffff' };
    }
    return { color1: '#000000', color2: '#ffffff' };
  });
  const [direction, setDirection] = useState(() => {
    if (!value?.includes('gradient')) return 'to bottom';
    const match = value.match(/linear-gradient\((to [^,]+),/);
    return match?.[1]?.trim() || 'to bottom';
  });

  const buildGradient = (c1, c2, dir) => `linear-gradient(${dir}, ${c1}, ${c2})`;

  const handleToggle = (gradient) => {
    setIsGradient(gradient);
    if (gradient) {
      onChange(buildGradient(gradientColors.color1, gradientColors.color2, direction));
    } else {
      onChange(gradientColors.color1);
    }
  };

  const handleGradientColor = (key, color) => {
    const updated = { ...gradientColors, [key]: color };
    setGradientColors(updated);
    onChange(buildGradient(updated.color1, updated.color2, direction));
  };

  const handleDirection = (dir) => {
    setDirection(dir);
    onChange(buildGradient(gradientColors.color1, gradientColors.color2, dir));
  };

  const previewStyle = value?.includes('gradient')
    ? { background: value }
    : { backgroundColor: value || '#ffffff' };

  return (
    <div className="space-y-2">
      {/* Solid / Gradient toggle */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleToggle(false)}
          className={`text-xs px-3 py-1.5 rounded-l-lg border transition-colors ${!isGradient ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
        >
          Solid
        </button>
        <button
          type="button"
          onClick={() => handleToggle(true)}
          className={`text-xs px-3 py-1.5 rounded-r-lg border transition-colors ${isGradient ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
        >
          Gradient
        </button>
      </div>

      {isGradient ? (
        <div className="space-y-2">
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Color 1</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={gradientColors.color1}
                  onChange={(e) => handleGradientColor('color1', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="#000000"
                />
                <input
                  type="color"
                  value={gradientColors.color1}
                  onChange={(e) => handleGradientColor('color1', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Color 2</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={gradientColors.color2}
                  onChange={(e) => handleGradientColor('color2', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="#ffffff"
                />
                <input
                  type="color"
                  value={gradientColors.color2}
                  onChange={(e) => handleGradientColor('color2', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                />
              </div>
            </div>
          </div>
          {/* Direction */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Direction</label>
            <div className="flex gap-1">
              {[
                { value: 'to top', label: '\u2191' },
                { value: 'to top right', label: '\u2197' },
                { value: 'to right', label: '\u2192' },
                { value: 'to bottom right', label: '\u2198' },
                { value: 'to bottom', label: '\u2193' },
                { value: 'to bottom left', label: '\u2199' },
                { value: 'to left', label: '\u2190' },
                { value: 'to top left', label: '\u2196' }
              ].map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => handleDirection(d.value)}
                  className={`w-8 h-8 rounded border text-sm transition-colors ${direction === d.value ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 items-start">
          <div className="flex-1">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="#FF5733 or rgb(255,87,51)"
            />
          </div>
          <input
            type="color"
            value={value && value.startsWith('#') && !value.includes('gradient') ? value : '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
          />
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className={`${height} w-full rounded-lg border`} style={previewStyle} />
      )}
    </div>
  );
};

const ImageOrColorPicker = ({ imageValue, colorValue, mode, onImageChange, onColorChange, onModeChange, label, height = 'h-32' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const token = getAcessToken();
      const response = await HitApiFormData(
        { file, userId: secretKey, projectKey },
        uploadSingleNew,
        'POST',
        { 'Authorization': `Bearer ${token}` }
      );
      if (response?.success && response?.file?.s3Url) {
        onImageChange(response.file.s3Url);
        toast.success(`${label} uploaded`);
      } else {
        toast.error('Failed to upload image');
      }
    } catch (err) {
      toast.error('Error uploading image');
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {/* Toggle */}
      <div className="flex items-center gap-1 mb-2">
        <button
          type="button"
          onClick={() => onModeChange('image')}
          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-l-lg border transition-colors ${mode === 'image' ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
        >
          <Image className="w-3 h-3" /> Image
        </button>
        <button
          type="button"
          onClick={() => onModeChange('color')}
          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-r-lg border transition-colors ${mode === 'color' ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
        >
          <Palette className="w-3 h-3" /> Color
        </button>
      </div>

      {mode === 'image' ? (
        imageValue ? (
          <div className="relative border rounded-lg overflow-hidden">
            <img src={imageValue} alt={label} className={`w-full ${height} object-cover`} />
            <button
              type="button"
              onClick={() => onImageChange('')}
              className="absolute top-2 right-2 p-1.5 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                <span className="text-xs text-gray-500 mt-1">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Click to upload {label}</span>
              </div>
            )}
          </div>
        )
      ) : (
        <ColorGradientPicker value={colorValue} onChange={onColorChange} height={height} />
      )}
    </div>
  );
};

const AddCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [form, setForm] = useState({
    name: '',
    icon: '',
    selectedIcon: '',
    iconType: 'image',
    iconColor: '',
    selectedIconColor: '',
    iconSize: 28,
    headerBanner: '',
    headerBannerType: 'image',
    headerBannerColor: '',
    headerTextColor: '',
    headerSubTextColor: '',
    categoryBackground: '',
    categoryBackgroundType: 'color',
    categoryBackgroundColor: '',
    promotionalBannerHeight: 140,
    promotionalBanner: [],
    animatedBannerHeight: 50,
    animatedBanner: [],
    sortOrder: 0,
    isActive: true
  });

  useEffect(() => {
    if (isEdit) fetchCategory();
  }, [id]);

  const fetchCategory = () => {
    setFetching(true);
    HitApi({ page: 1, limit: 100000, search: '' }, searchAppCategory)
      .then((res) => {
        const docs = res?.data?.docs || res?.data || [];
        const cat = Array.isArray(docs) ? docs.find((c) => c._id === id) : null;
        if (cat) {
          setForm({
            name: cat.name || '',
            icon: cat.icon || '',
            selectedIcon: cat.selectedIcon || '',
            iconType: cat.iconType || 'image',
            iconColor: cat.iconColor || '',
            selectedIconColor: cat.selectedIconColor || '',
            iconSize: cat.iconSize || 28,
            headerBanner: cat.headerBanner || '',
            headerBannerType: cat.headerBannerType || 'image',
            headerBannerColor: cat.headerBannerColor || '',
            headerTextColor: cat.headerTextColor || '',
            headerSubTextColor: cat.headerSubTextColor || '',
            categoryBackground: cat.categoryBackground || '',
            categoryBackgroundType: cat.categoryBackgroundType || 'color',
            categoryBackgroundColor: cat.categoryBackgroundColor || '',
            promotionalBannerHeight: cat.promotionalBannerHeight || 140,
            animatedBannerHeight: cat.animatedBannerHeight || 50,
            promotionalBanner: (cat.promotionalBanner || []).map((b) => ({
              image: b.image || '',
              imageType: b.imageType || 'image',
              imageColor: b.imageColor || '',
              title: b.title || '',
              subtitle: b.subtitle || '',
              textColor: b.textColor || '',
              link: b.link || '',
              isActive: b.isActive !== undefined ? b.isActive : true
            })),
            animatedBanner: (cat.animatedBanner || []).map((b) => ({
              text: b.text || '',
              icon: b.icon || '',
              iconType: b.iconType || (b.color && !b.icon ? 'color' : 'image'),
              color: b.color || '',
              textColor: b.textColor || '',
              isActive: b.isActive !== undefined ? b.isActive : true
            })),
            sortOrder: cat.sortOrder || 0,
            isActive: cat.isActive !== undefined ? cat.isActive : true
          });
        } else {
          toast.error('Category not found');
          navigate('/app-control/category');
        }
      })
      .catch(() => toast.error('Failed to fetch category'))
      .finally(() => setFetching(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);
    const payload = isEdit ? { _id: id, ...form } : form;
    const api = isEdit ? updateAppCategory : addAppCategory;

    HitApi(payload, api)
      .then((res) => {
        if (res?.success || res?.statusCode === 200 || res?.statusCode === 201) {
          toast.success(isEdit ? 'Category updated successfully' : 'Category created successfully');
          navigate('/app-control/category');
        } else {
          toast.error(res?.message || 'Operation failed');
        }
      })
      .catch(() => toast.error('Something went wrong'))
      .finally(() => setLoading(false));
  };

  const addPromoBanner = () => {
    setForm((prev) => ({
      ...prev,
      promotionalBanner: [...prev.promotionalBanner, { image: '', imageType: 'image', imageColor: '', title: '', subtitle: '', textColor: '', link: '', isActive: true }]
    }));
  };

  const removePromoBanner = (index) => {
    setForm((prev) => ({
      ...prev,
      promotionalBanner: prev.promotionalBanner.filter((_, i) => i !== index)
    }));
  };

  const updatePromoBanner = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.promotionalBanner];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, promotionalBanner: updated };
    });
  };

  const addAnimatedBanner = () => {
    setForm((prev) => ({
      ...prev,
      animatedBanner: [...prev.animatedBanner, { text: '', icon: '', iconType: 'image', color: '', textColor: '', isActive: true }]
    }));
  };

  const removeAnimatedBanner = (index) => {
    setForm((prev) => ({
      ...prev,
      animatedBanner: prev.animatedBanner.filter((_, i) => i !== index)
    }));
  };

  const updateAnimatedBanner = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.animatedBanner];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, animatedBanner: updated };
    });
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app-control/category')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Category' : 'Add Category'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update category details' : 'Create a new app category'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Enter category name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon Size (px)</label>
              <input
                type="number"
                value={form.iconSize}
                onChange={(e) => setForm({ ...form, iconSize: parseInt(e.target.value) || 28 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="28"
                min="12"
                max="100"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <label className="text-sm font-medium text-gray-700">Active</label>
            <button
              type="button"
              onClick={() => setForm({ ...form, isActive: !form.isActive })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Icon */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Category Icons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Non-Selected Icon</label>
              <ImageOrColorPicker
                imageValue={form.icon}
                colorValue={form.iconColor}
                mode={form.iconType}
                onImageChange={(url) => setForm({ ...form, icon: url })}
                onColorChange={(color) => setForm({ ...form, iconColor: color })}
                onModeChange={(mode) => setForm({ ...form, iconType: mode })}
                label="Icon"
                height="h-24"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Selected Icon</label>
              <ImageOrColorPicker
                imageValue={form.selectedIcon}
                colorValue={form.selectedIconColor}
                mode={form.iconType}
                onImageChange={(url) => setForm({ ...form, selectedIcon: url })}
                onColorChange={(color) => setForm({ ...form, selectedIconColor: color })}
                onModeChange={(mode) => setForm({ ...form, iconType: mode })}
                label="Selected Icon"
                height="h-24"
              />
            </div>
          </div>
        </div>

        {/* Header Banner */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Header Banner</h2>
          <ImageOrColorPicker
            imageValue={form.headerBanner}
            colorValue={form.headerBannerColor}
            mode={form.headerBannerType}
            onImageChange={(url) => setForm({ ...form, headerBanner: url })}
            onColorChange={(color) => setForm({ ...form, headerBannerColor: color })}
            onModeChange={(mode) => setForm({ ...form, headerBannerType: mode })}
            label="Header Banner"
            height="h-40"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Text Color</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.headerTextColor}
                  onChange={(e) => setForm({ ...form, headerTextColor: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="#ffffff"
                />
                <input
                  type="color"
                  value={form.headerTextColor && form.headerTextColor.startsWith('#') ? form.headerTextColor : '#000000'}
                  onChange={(e) => setForm({ ...form, headerTextColor: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Sub Text Color</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.headerSubTextColor}
                  onChange={(e) => setForm({ ...form, headerSubTextColor: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="#888888"
                />
                <input
                  type="color"
                  value={form.headerSubTextColor && form.headerSubTextColor.startsWith('#') ? form.headerSubTextColor : '#888888'}
                  onChange={(e) => setForm({ ...form, headerSubTextColor: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Category Background */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search & Category Background</h2>
          <ImageOrColorPicker
            imageValue={form.categoryBackground}
            colorValue={form.categoryBackgroundColor}
            mode={form.categoryBackgroundType}
            onImageChange={(url) => setForm({ ...form, categoryBackground: url })}
            onColorChange={(color) => setForm({ ...form, categoryBackgroundColor: color })}
            onModeChange={(mode) => setForm({ ...form, categoryBackgroundType: mode })}
            label="Category Background"
            height="h-32"
          />
        </div>

        {/* Promotional Banners */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Promotional Banners</h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Height (px)</label>
                <input
                  type="number"
                  value={form.promotionalBannerHeight}
                  onChange={(e) => setForm({ ...form, promotionalBannerHeight: parseInt(e.target.value) || 140 })}
                  className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  min="50"
                  max="500"
                />
              </div>
              <button
                type="button"
                onClick={addPromoBanner}
                className="flex items-center gap-1 text-sm px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Banner
              </button>
            </div>
          </div>

          {form.promotionalBanner.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No promotional banners added</p>
          )}

          <div className="space-y-4">
            {form.promotionalBanner.map((banner, index) => (
              <div key={index} className={`border rounded-lg p-4 relative ${banner.isActive === false ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
                  <button
                    type="button"
                    onClick={() => updatePromoBanner(index, 'isActive', !banner.isActive)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${banner.isActive !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${banner.isActive !== false ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePromoBanner(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Banner Background</label>
                  <ImageOrColorPicker
                    imageValue={banner.image}
                    colorValue={banner.imageColor}
                    mode={banner.imageType}
                    onImageChange={(url) => updatePromoBanner(index, 'image', url)}
                    onColorChange={(color) => updatePromoBanner(index, 'imageColor', color)}
                    onModeChange={(mode) => updatePromoBanner(index, 'imageType', mode)}
                    label="Promo Image"
                    height="h-28"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Title</label>
                    <input
                      type="text"
                      value={banner.title}
                      onChange={(e) => updatePromoBanner(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Banner title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={banner.subtitle}
                      onChange={(e) => updatePromoBanner(index, 'subtitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Banner subtitle"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Text Color</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={banner.textColor}
                        onChange={(e) => updatePromoBanner(index, 'textColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="#ffffff"
                      />
                      <input
                        type="color"
                        value={banner.textColor && banner.textColor.startsWith('#') ? banner.textColor : '#000000'}
                        onChange={(e) => updatePromoBanner(index, 'textColor', e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Link</label>
                    <input
                      type="text"
                      value={banner.link}
                      onChange={(e) => updatePromoBanner(index, 'link', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Animated Banners */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Animated Banners</h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Height (px)</label>
                <input
                  type="number"
                  value={form.animatedBannerHeight}
                  onChange={(e) => setForm({ ...form, animatedBannerHeight: parseInt(e.target.value) || 50 })}
                  className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  min="20"
                  max="200"
                />
              </div>
              <button
                type="button"
                onClick={addAnimatedBanner}
                className="flex items-center gap-1 text-sm px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Banner
              </button>
            </div>
          </div>

          {form.animatedBanner.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No animated banners added</p>
          )}

          <div className="space-y-4">
            {form.animatedBanner.map((banner, index) => (
              <div key={index} className={`border rounded-lg p-4 relative ${banner.isActive === false ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
                  <button
                    type="button"
                    onClick={() => updateAnimatedBanner(index, 'isActive', !banner.isActive)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${banner.isActive !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${banner.isActive !== false ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAnimatedBanner(index)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Banner Icon</label>
                  <ImageOrColorPicker
                    imageValue={banner.icon}
                    colorValue={banner.color}
                    mode={banner.iconType || 'image'}
                    onImageChange={(url) => updateAnimatedBanner(index, 'icon', url)}
                    onColorChange={(color) => updateAnimatedBanner(index, 'color', color)}
                    onModeChange={(mode) => updateAnimatedBanner(index, 'iconType', mode)}
                    label="Icon"
                    height="h-20"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Text</label>
                    <input
                      type="text"
                      value={banner.text}
                      onChange={(e) => updateAnimatedBanner(index, 'text', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Banner text"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Text Color</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={banner.textColor}
                        onChange={(e) => updateAnimatedBanner(index, 'textColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="#ffffff"
                      />
                      <input
                        type="color"
                        value={banner.textColor && banner.textColor.startsWith('#') ? banner.textColor : '#000000'}
                        onChange={(e) => updateAnimatedBanner(index, 'textColor', e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/app-control/category')}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : isEdit ? 'Update Category' : 'Create Category'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCategory;
